import React, { useEffect, useReducer } from 'react';

import { ParsedUrlQueryInput } from 'querystring';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { Button, CardGrid, EmptyState, Flexbox, Text } from '@morressier/ts';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { format, isValid, lightFormat, startOfDay } from 'date-fns';
import { BiCalendarX } from 'react-icons/bi';
import { FaFilter } from 'react-icons/fa';
import useSWR from 'swr';

import { AgendaFeedResponse, getAgendaFeed } from 'api/events';
import { SessionQueryParamsV3, TimeFilterStrings } from 'api/session';
import { parseQuery } from 'api/utils';
import { Drawer } from 'components/FilterDrawer/style';
import { FilterArgs, FilterSidebar } from 'components/FilterSidebar';
import { CustomButton, FilterBoxContainer } from 'components/FilterSidebar/styles';
import { cleanAllFilters, getTimeFilter } from 'components/FilterSidebar/utils';
import { GridSkeleton } from 'components/Skeleton/Grid';
import { SessionsListItem } from 'containers/SessionsList/SessionsListItem';
import { PageContainer, PageTitle } from 'containers/styles';
import { PageProps } from 'pages/event/[eventId]/my-agenda';
import { useAuthContext } from 'store';
import { getEventDays } from 'utils/dates';
import { logEvent } from 'utils/eventTracking';
import { useFeatureFlagContext } from 'utils/unleashFeatureFlags/FeatureFlagProvider';
import { FEATURE_FLAGS } from 'utils/unleashFeatureFlags/flags';

import * as Styles from '../SessionsList/styles';
import { MeetingCardItem } from './MeetingCardItem';
import { groupSessionsByDay } from './util';

export enum MyAgendaFilters {
  ShowAll = 'all',
  Sessions = 'sessions',
  Meetings = 'meetings',
}

export const getTypeFilter = (type?: MyAgendaFilters) => {
  const items = Object.values(MyAgendaFilters);
  if (type && items.includes(type as MyAgendaFilters)) {
    return type;
  }

  return MyAgendaFilters.ShowAll;
};

export const MyAgendaContainer: React.FC<PageProps['data']> = ({ event }) => {
  const { features } = useFeatureFlagContext();
  const meetingSchedulerEnabled = !!features[FEATURE_FLAGS.EVT_MEETING_SCHEDULER];
  const { query: _query, pathname } = useRouter();
  const query = _query as SessionQueryParamsV3<MyAgendaFilters>;
  const { eventId, page_size, page, ...restQuery } = query;
  const { q: _searchQuery = '', ...restQueryWithoutSearch } = restQuery;
  const [isDrawerOpen, toggleDrawerOpen] = useReducer(state => !state, false);
  const { state: auth } = useAuthContext();

  // If query.time is a date, get events from previous response
  // If it is 'upcoming', 'past' etc. make a request to BE
  const sendTimeRequest = Object.values(TimeFilterStrings).includes(
    query.time as TimeFilterStrings,
  );
  const queryTime = !sendTimeRequest
    ? query.time && new Date(query.time as string).toISOString()
    : undefined;

  const {
    data: agendaResponse,
    mutate: mutateFeed,
    isValidating,
  } = useSWR(
    `${eventId}/my-agenda?userId=${auth?.profile?.id}&type=${query.type}${
      sendTimeRequest ? `&time=${query.time}` : ''
    }`,
    () =>
      getAgendaFeed(eventId!, {
        userId: auth?.profile?.id,
        type: query.type as string,
        time: sendTimeRequest ? (query.time as string) : undefined,
      }),
  );
  const sessionsByDateInUserTimezone = groupSessionsByDay(agendaResponse as AgendaFeedResponse);
  const totalDates = agendaResponse
    ? getEventDays(Object.keys(sessionsByDateInUserTimezone).sort())
    : [];
  const feed = groupSessionsByDay(
    queryTime ? sessionsByDateInUserTimezone[queryTime] : sessionsByDateInUserTimezone,
  ) as AgendaFeedResponse;

  useEffect(() => {
    logEvent('MY_AGENDA_PAGE_VISITED', { event_id: eventId });
  }, []);

  const refetchPage = () => {
    mutateFeed();
  };

  // Count all scheduled items
  const totalFilterResults = feed
    ? Object.values(feed).reduce((sum, meetingsPerDate) => sum + (meetingsPerDate?.length || 0), 0)
    : 0;

  const theme = useTheme();
  const isBelowXs = !useMediaQuery(theme.breakpoints.up('xs'));
  const isBelowLg = useMediaQuery(theme.breakpoints.down('md'));

  const timeFilter = getTimeFilter(query.time);
  const appliedFiltersCount = Object.keys(
    cleanAllFilters(parseQuery(restQueryWithoutSearch)),
  ).length;

  const handleFiltersChange = ({
    q = query?.q,
    type = query?.type,
    time = query?.time,
  }: FilterArgs<MyAgendaFilters>) => {
    const { page: _p, page_size: _ps, search, ...rest } = query;

    return {
      pathname,
      query: cleanAllFilters(
        parseQuery({
          ...rest,
          eventId,
          time,
          type,
          featured: 'all',
          q,
        }),
      ) as ParsedUrlQueryInput,
    };
  };
  const typeFilter = getTypeFilter(query.type);

  const filters = (
    <>
      <Link shallow href={handleFiltersChange({ type: MyAgendaFilters.ShowAll })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === MyAgendaFilters.ShowAll} />
          <Text>All Types</Text>
        </Flexbox>
      </Link>

      <Link shallow href={handleFiltersChange({ type: MyAgendaFilters.Sessions })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === MyAgendaFilters.Sessions} />
          <Text>Sessions</Text>
        </Flexbox>
      </Link>

      <Link shallow href={handleFiltersChange({ type: MyAgendaFilters.Meetings })}>
        <Flexbox alignItems="center" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
          <Styles.RadioBox isChecked={typeFilter === MyAgendaFilters.Meetings} />
          <Text>Meetings</Text>
        </Flexbox>
      </Link>
    </>
  );

  return meetingSchedulerEnabled ? (
    <PageContainer withTopMargin>
      <Grid container spacing={4}>
        {isBelowLg ? (
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            BackdropProps={{ style: { opacity: 0.5, backgroundColor: '#424D5D' } }}
            onBackdropClick={toggleDrawerOpen}
          >
            <FilterSidebar
              event={event}
              isBelowLg={isBelowLg}
              dates={totalDates}
              onClose={toggleDrawerOpen}
              totalFilterResults={totalFilterResults}
              handleFiltersChange={handleFiltersChange}
              showUserAgendaToggle={false}
              showOnlyFeaturedToggle={false}
              filters={filters}
              showTags={false}
            />
          </Drawer>
        ) : (
          <Grid lg={3} item>
            <FilterSidebar
              event={event}
              dates={totalDates}
              onClose={toggleDrawerOpen}
              totalFilterResults={totalFilterResults}
              handleFiltersChange={handleFiltersChange}
              showUserAgendaToggle={false}
              showOnlyFeaturedToggle={false}
              filters={filters}
              showTags={false}
            />
          </Grid>
        )}

        <Grid xs={12} lg={9} item>
          <PageTitle>Agenda</PageTitle>

          {isBelowLg && (
            <FilterBoxContainer>
              <Flexbox flexWrap="wrap">
                {isBelowXs ? (
                  <CustomButton
                    onClick={toggleDrawerOpen}
                    selected={timeFilter === TimeFilterStrings.Past}
                    round
                    fullWidth
                  >
                    <FaFilter
                      color="#424D5D"
                      style={{ marginRight: '10px', marginBottom: '-2px' }}
                    />
                    Filters {!!appliedFiltersCount && `(${appliedFiltersCount})`}
                  </CustomButton>
                ) : (
                  <>
                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.ShowAll })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.ShowAll} round>
                        Show All
                      </CustomButton>
                    </Link>

                    <Link
                      shallow
                      href={handleFiltersChange({ time: TimeFilterStrings.HappeningNow })}
                    >
                      <CustomButton selected={timeFilter === TimeFilterStrings.HappeningNow} round>
                        Happening Now
                      </CustomButton>
                    </Link>

                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.Upcoming })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.Upcoming} round>
                        Upcoming
                      </CustomButton>
                    </Link>

                    <Link shallow href={handleFiltersChange({ time: TimeFilterStrings.Past })}>
                      <CustomButton selected={timeFilter === TimeFilterStrings.Past} round>
                        Past Sessions
                      </CustomButton>
                    </Link>

                    <CustomButton onClick={toggleDrawerOpen} round>
                      <FaFilter
                        color="#424D5D"
                        style={{ marginRight: '10px', marginBottom: '-2px' }}
                      />
                      More filters {!!appliedFiltersCount && `(${appliedFiltersCount})`}
                    </CustomButton>
                  </>
                )}
              </Flexbox>
            </FilterBoxContainer>
          )}

          <Text color="grey">
            {`${totalFilterResults} Session${totalFilterResults === 1 ? '' : 's'}.`}
            {!!_query.length && (
              <>
                <span>To see the full Agenda</span>
                <span>Clear filters</span>
              </>
            )}
          </Text>
          {!feed && isValidating ? (
            <GridSkeleton />
          ) : (
            feed &&
            Object.keys(feed!).map(date => {
              const dayString = format(new Date(date), 'EEE, LLL d');

              return (
                <div key={date} className="mb4 gap-vert-2">
                  {!isValid(timeFilter) && (
                    <div className="mb2">
                      <Text size="body1_new" fontWeight="bold">
                        {dayString}
                      </Text>
                    </div>
                  )}
                  <CardGrid>
                    {feed[date].map(item => {
                      if (item.scheduledItemType === 'SESSION') {
                        return (
                          <SessionsListItem
                            // @ts-ignore
                            session={item}
                            eventId={eventId!}
                            cardMode="List"
                            deleteCallBack={refetchPage}
                          />
                        );
                      }

                      return <MeetingCardItem meeting={item} mutate={refetchPage} event={event} />;
                    })}
                  </CardGrid>
                </div>
              );
            })
          )}

          {Object.keys(feed || {}).length === 0 && (
            <div style={{ marginTop: '4.5rem' }}>
              <EmptyState
                icon={<BiCalendarX size="48px" color="lightGrey" />}
                title="You have nothing scheduled"
                subtitle="Save Sessions and schedule Meetings."
              >
                <Link href={`/event/${eventId}/sessions`}>
                  <Button>Search Sessions</Button>
                </Link>
              </EmptyState>
            </div>
          )}
        </Grid>
      </Grid>
    </PageContainer>
  ) : null;
};
